import type { AxiosResponse } from 'axios';
import { useTranslation } from 'next-i18next';
import type { FC } from 'react';
import React, { useEffect } from 'react';
import { BaseTextButton, BaseTextField } from '@evrone-erp/uikit';
import { Box } from '@mui/material';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { TBaseOnSuccess } from '@shared/lib';
import { capitalizeFirstLetter, getErrorDetails, getErrorMessage } from '@shared/lib';
import { PlateFormContainer } from '@shared/ui';
import { useSetPermanentPassword } from '@features/auth/api';
import type { TSetPermanentPasswordFormFields } from '@features/auth/lib/types';
import { getTranslatedScheme } from '@features/auth/lib/setPermanentPasswordScheme';
const DEFAULT_VALUES: TSetPermanentPasswordFormFields = {
  password: ''
};
export const SetPermanentPasswordForm: FC<TBaseOnSuccess<AxiosResponse>> = ({
  onSuccess
}) => {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    control,
    handleSubmit,
    trigger,
    formState: {
      errors: formErrors
    }
  } = useForm<TSetPermanentPasswordFormFields>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(getTranslatedScheme(t))
  });
  const [password] = useWatch({
    control,
    name: ['password']
  });
  useEffect(() => {
    trigger();
  }, [i18n.language, trigger]);
  const {
    mutate: setPermanentPassword,
    error,
    isError,
    isLoading
  } = useSetPermanentPassword({
    options: {
      onSuccess
    }
  });
  const isSubmitDisabled = !password || isLoading;
  const clientErrorText = formErrors?.password?.message;
  const serverErrorText = isError && error && `${capitalizeFirstLetter(getErrorMessage(error) as string)}: ${getErrorDetails(error)}`;
  const formErrorText = clientErrorText || serverErrorText;
  const handleSuccessSubmit: SubmitHandler<TSetPermanentPasswordFormFields> = data => {
    setPermanentPassword({
      newPassword: data.password.trim()
    });
  };
  return <form onSubmit={handleSubmit(handleSuccessSubmit)}>
      <PlateFormContainer formTitle={t('welcomeBack')} headerTitle={t('setPassword')} headerSubtitle={t('passwordRequirements')} errorContent={formErrorText} footerPrimaryAction={<BaseTextButton type="submit" disabled={isSubmitDisabled} isLoading={isLoading} iconType="plus">
            {t('confirm')}
          </BaseTextButton>}>
        <Box>
          <Controller control={control} name="password" render={({
          field
        }) => <BaseTextField type="password" name={field.name} label={t('password')} value={field.value} fullWidth onChange={field.onChange} onBlur={field.onBlur} />} />
        </Box>
      </PlateFormContainer>
    </form>;
};